'use client'

import { useEffect, useState } from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import { CompanyFormFields } from '@/root/modules/shared/ConfirmPage/const'
import type { CustomerType } from '@/root/modules/shared/ConfirmPage/types'
import { useDefaultCountry } from '@/root/modules/shared/ConfirmPage/hooks/useDefaultCountry'

interface UseCustomerInfoViewProps {
  companyForm?: boolean
  privatePersonForm?: boolean
  isAuth: boolean
  customerType: CustomerType
  selectedRoleType?: CustomerType
  setValue: UseFormSetValue<FieldValues>
  clearErrors: (name?: string) => void
}

/** Which blocks of the customer form are visible, and the "add / remove company details" toggle. */
export const useCustomerInfoView = ({
  companyForm,
  privatePersonForm,
  isAuth,
  customerType,
  selectedRoleType,
  setValue,
  clearErrors,
}: UseCustomerInfoViewProps) => {
  const { defaultCountryCode } = useDefaultCountry()

  const showBothOptions = companyForm && privatePersonForm

  // Company form is readonly when a company from profile is selected in dropdown
  const isProfileCompanySelected = isAuth && selectedRoleType === 'company'

  // For private person: company form is hidden behind a toggle
  const isPrivateMode = customerType === 'private'
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(false)

  // Collapse company form when switching to private mode
  useEffect(() => {
    if (isPrivateMode) {
      setIsCompanyExpanded(false)
    }
  }, [isPrivateMode])

  const handleToggleCompany = () => {
    if (isCompanyExpanded) {
      // Collapse: clear company fields and errors
      Object.values(CompanyFormFields).forEach((field) => {
        if (field === 'country') {
          setValue(field, defaultCountryCode)
        } else {
          setValue(field, '')
        }
        clearErrors(field)
      })
    }
    setIsCompanyExpanded((prev) => !prev)
  }

  // Determine if company form should be visible
  const showCompanyForm = companyForm && (isProfileCompanySelected || !isPrivateMode || isCompanyExpanded)

  return {
    showBothOptions,
    isProfileCompanySelected,
    isCompanyExpanded,
    handleToggleCompany,
    showCompanyForm,
    // Toggle button for company form — only for auth users in private mode (guests use radio buttons)
    showCompanyToggle: companyForm && isAuth && isPrivateMode && !isProfileCompanySelected,
  }
}
